import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, Data, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { DashboardLayoutService } from '../../../services';
import { RouterTabDirective } from '../router-tab/router-tab.directive';
import { RouterTabsDirective } from '../router-tab/router-tabs.directive';
import { Breadcrumb, BreadcrumbMenuItem } from './breadcrumb';
import { map } from 'rxjs';

const MAX_HISTORY = 7;

@Component({
	selector: 'breadcrumb',
	standalone: true,
	imports: [
		RouterLink,
		RouterLinkActive,
		MatTabsModule,
		MatMenuModule,
		MatButtonModule,
		RouterTabsDirective,
		RouterTabDirective,
	],
	styleUrls: ['./breadcrumb.part.scss'],
	templateUrl: './breadcrumb.part.html'
})
export class BreadcrumbPart implements OnInit, OnDestroy {

	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private title = inject(Title);
	public layoutService = inject(DashboardLayoutService);

	public breadcrumbs = signal<Breadcrumb[]>([]);
	public tabs = signal<BreadcrumbMenuItem[]>([]);

	private upUrl = '..';

	ngOnInit() {
		this.reset();
		this.handleNavigation();

		this.router.events.subscribe(async event => {
			if (event instanceof NavigationEnd) {
				this.handleNavigation();
			}
		});

	}


	private handleNavigation() {
		this.buildCrumbs();
		this.buildTitle();

		const deepestRouteData = this.getDeepestRouteData(this.router.routerState.root);
		this.upUrl = deepestRouteData['upUrl'] ?? '..';	
	}


	private getDeepestRouteData(route: ActivatedRoute): Data {
		if (route.firstChild) {			return this.getDeepestRouteData(route.firstChild);		}
		return route.snapshot.data;
	}


	ngOnDestroy() {
		this.reset();
	}


	reset() {
		this.breadcrumbs.set([]);
		this.tabs.set([]);
		this.title.setTitle('EforAll');
	}


	buildCrumbs() {
		this.breadcrumbs.set([]);
		this.tabs.set([]);

		//
		// Recursively build the breadcrumbs
		//
		this.buildCrumb(this.router.routerState.snapshot.root, []);


		//
		// Add a header menu item to each breadcrumb menu
		//
		if (this.breadcrumbs().length) this.breadcrumbs()[0]?.menu?.unshift({
			label: 'Home',
			path: '/',
			includeInTabs: false,
		});


		for (let b = 1; b < this.breadcrumbs().length; b++) {
			const prior = this.breadcrumbs()[b - 1];
			this.breadcrumbs()[b]?.menu?.unshift({
				label: prior!.label,
				path: prior!.path,
				includeInTabs: false,
			});
		}

		//
		// Tabs are always the last breadcrumb menu
		//
		if (this.breadcrumbs().length) {
			this.tabs.set((this.breadcrumbs()[this.breadcrumbs().length - 1]!
				.menu || [])
				.filter(i => i.includeInTabs)
				.map(m => {
					const tab = Object.assign({}, m);
					tab.label = tab.tabLabel || tab.label; // The tab might have a different label than the menu
					return tab;
				}));

			this.layoutService.setShowNavTabsAuto({ hasMultipleTabs: this.tabs().length > 1 });
		}


		if (!this.breadcrumbs().length) {
			this.breadcrumbs.set([
				{
					label: 'Home',
					menu: [{
						label: 'Home',
						path: '/',
						includeInTabs: false,
					}],
					path: '/',
				}]);
		}
	}


	private buildCrumb(node: ActivatedRouteSnapshot, menu: BreadcrumbMenuItem[], url = '') {
		const urlPaths = this.router.url.split('/');

		//
		// Get the text of the breadcrumb if there is one
		//
		let menuLabel: string = (node.data && node.data['breadcrumb']);
		let tabLabel: string | undefined = undefined;

		//
		// If this route has any url segments, join them into one segment
		//
		let urlSegment: string | undefined = node.url.map<string>(urlSegment => { return urlSegment.path }).join('/');
		if (urlSegment == '') urlSegment = undefined;

		if (urlSegment) {

			const slug = urlSegment;
			url = urlSegment = url + '/' + urlSegment;

			if (menuLabel) {

				if (menuLabel == ':slug') {
					menuLabel = slug;
					tabLabel = slug;
				}

				if (!menu?.length) {
					menu = [{
						label: menuLabel,
						path: urlSegment,
						includeInTabs: false,
					}];
				}

				const crumb = {
					label: menuLabel,
					tabLabel,
					path: urlSegment,
					menu,
				};
				this.breadcrumbs.set([...this.breadcrumbs(), crumb]);
			}
		}

		//
		// Use the routeConfig children to determine the available child paths
		//
		let nodemenu: BreadcrumbMenuItem[] = [];

		if (node.routeConfig && node.routeConfig.children) {
			nodemenu = node.routeConfig.children
				.filter(child => child.path && child.data && child.data['breadcrumb'])
				.filter(child => child.path?.indexOf('/') == -1) /* skip anything with an embedded child path */
				.filter(child => child.path?.indexOf(':') == -1) /* skip anything with a variable */
				.map(child => {
					const crumb = {
						label: child.data?.['breadcrumb'],
						path: url + '/' + child.path,
						includeInTabs: !child.data?.['noTab'],
					};

					return crumb;
				});
		}

		if (nodemenu.length) {
			nodemenu = nodemenu.sort((a, b) => {
				// dynamic fields go to the end of the list
				const dynamicA = a.label.indexOf(':') > -1;
				const dynamicB = b.label.indexOf(':') > -1;
				if (dynamicA && !dynamicB) return 1;
				if (dynamicB && !dynamicA) return -1;
				return 0; // a.name.localeCompare(b.name);
			});
		}

		if (node.firstChild) {
			this.buildCrumb(node.firstChild, nodemenu, url);
		}
	}


	/**
	 * Create and add a title (shown in the browser tab)
	 */
	async buildTitle() {
		//
		// Synchronously don't try to map and keys, so we get something fast. This will be English
		//
		let title = this.breadcrumbs().map(breadcrumb => breadcrumb.label).join(' > ');
		this.title.setTitle(title);

		//
		// Asynchronously wait for labels to load and then set translated values
		//
		title = this.breadcrumbs().map(breadcrumb => {
			return breadcrumb.label;
		}).join(' > ');

		this.title.setTitle(title);
	}


	getCachedMenu(keySuffix: string, name: string, url: string, tabLabel: string | undefined = undefined): BreadcrumbMenuItem[] {

		// Change the prefix to forcefully reset everyone's cached links
		const key = 'VGaryTest-' + keySuffix;

		const item: BreadcrumbMenuItem = {
			label: name,
			tabLabel,
			path: url,
			includeInTabs: true,
		};

		// Get prior menu
		let menu: BreadcrumbMenuItem[] = JSON.parse(localStorage.getItem(key) || '[]');

		// Remove the current item from the prior menu if it is there
		menu = menu.filter(i => i.path !== item.path);

		// Make room for our new one
		if (menu.length >= MAX_HISTORY) menu.shift();

		// Add our new one
		menu.push(item);

		// Remember for later
		localStorage.setItem(key, JSON.stringify(menu));

		// Only the last one is shown in the nav tabs
		menu.forEach(m => m.includeInTabs = false);
		menu[menu.length - 1]!.includeInTabs = true;

		// Sort it
		menu.sort((a, b) => {
			return a.label < b.label ? -1 : 1;
		});

		return menu;
	}


	goUp() {
		const url = this.combineUrls(this.router.routerState.snapshot.url, this.upUrl);
		this.router.navigateByUrl(url);
	}


	combineUrls(one: string, two: string): string {
		const a = one.split('/');
		const b = two.split('/');

		if (two == '..') { // simply go up a level
			a.pop();

			//
			// If the last segment is a number than it is an ID. If we are going up
			// one level, we should skip passed the ID (go up two levels)
			//
			const lastSegment = +a[a.length - 1]!;
			if (isNaN(lastSegment) == false) a.pop();
		} else {
			//
			// This handles a more complicated combination
			//
			for (let segment of b) {
				segment = segment.trim();
				if (segment == '') continue;
				if (segment == '.') continue;
				if (segment == '..') a.pop();
				else a.push(segment);
			}
		}

		return a.join('/');
	}


	getBreadcrumbClasses(i: number) {
		const c = this.breadcrumbs().length - i;
		if (c >= 6) return 'breadcrumb-item d-none';
		if (c == 5) return 'breadcrumb-item d-none d-xl-block';
		if (c == 4) return 'breadcrumb-item d-none d-lg-block';
		if (c == 3) return 'breadcrumb-item d-none d-sm-block';
		return 'breadcrumb-item';
	}


	isMenuItemSelected(breadcrumb: Breadcrumb, menu: BreadcrumbMenuItem) {
		if (breadcrumb.label == menu.label) return true;
		if (breadcrumb.label.length == 4) { // hackey way to check for a site code
			return menu.label.indexOf(`(${breadcrumb.label})`) >= 0;
		}
		return false;
	}


	async toggleNavTabs() {
		this.layoutService.showNavTabsVisibility.set(!this.layoutService.showNavTabs() ? 'Show' : 'Hide');
	}
}